export type AuthSessionChangeType = 'tokens-set' | 'tokens-cleared'

const CHANNEL_NAME = 'laplace-auth-session'

let revision = 0
let channel: BroadcastChannel | null = null
let suppressNotify = false

const changeListeners = new Set<(type: AuthSessionChangeType) => void>()
const revisionListeners = new Set<(revision: number) => void>()

function bumpRevision(): void {
  revision += 1
  revisionListeners.forEach((listener) => {
    listener(revision)
  })
}

function initChannel(): void {
  if (channel !== null) {
    return
  }

  if (typeof BroadcastChannel === 'undefined') {
    return
  }

  try {
    channel = new BroadcastChannel(CHANNEL_NAME)
    channel.onmessage = (event: MessageEvent<unknown>) => {
      const type = event.data
      if (type !== 'tokens-set' && type !== 'tokens-cleared') {
        return
      }

      bumpRevision()
      changeListeners.forEach((listener) => {
        listener(type)
      })
    }
  } catch {
    channel = null
  }
}

export function notifyAuthSessionChange(type: AuthSessionChangeType): void {
  if (suppressNotify) {
    return
  }

  initChannel()
  bumpRevision()

  try {
    channel?.postMessage(type)
  } catch {
    // Broadcast unavailable — local revision bump still updates this tab.
  }
}

export function subscribeToAuthSessionChanges(
  listener: (type: AuthSessionChangeType) => void,
): () => void {
  initChannel()
  changeListeners.add(listener)

  return () => {
    changeListeners.delete(listener)
  }
}

export function subscribeToAuthTokensRevision(
  listener: (revision: number) => void,
): () => void {
  initChannel()
  listener(revision)
  revisionListeners.add(listener)

  return () => {
    revisionListeners.delete(listener)
  }
}

export function runWithoutAuthSessionNotify(action: () => void): void {
  suppressNotify = true

  try {
    action()
  } finally {
    suppressNotify = false
  }
}
